import { enrichWellListWithKeysAndUnifiedNames } from 'helpers/WellHelper';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateAuthorizedWellsAndWellbores } from 'redux/Wells';
import { useSelectAuthorizedWellbores, useSelectAuthorizedWells } from 'redux/Wells/selectors';
import { getErrorMessage } from 'services/networking/errorHandler';
import wellsWithWellboreApiClient from 'services/networking/wellsWithWellboreApiClient';
import { InnerSensorWellboreWithWellInfo } from 'types/wellbore';

let wellsAndWellboresLoading = false;
let wellsAndWellboresLoaded = false;

export const useFetchWellsWithWellbores = () => {
  const wells = useSelectAuthorizedWells();
  const dispatch = useDispatch();

  const fetchWells = useCallback(async () => {
    wellsAndWellboresLoading = true;
    const { response, error } = await wellsWithWellboreApiClient.getWellsWithWellbores();
    const wellsWithKeys = enrichWellListWithKeysAndUnifiedNames(response);
    wellsAndWellboresLoaded = true;
    if (error) toast.error(getErrorMessage(error));
    wellsAndWellboresLoading = false;
    dispatch(updateAuthorizedWellsAndWellbores(wellsWithKeys));
  }, [dispatch]);

  const getWells = useCallback(() => {
    if (!wellsAndWellboresLoaded && !wellsAndWellboresLoading) fetchWells();
  }, [fetchWells]);

  useEffect(() => {
    getWells();
  }, [getWells]);

  const wellbores = useSelectAuthorizedWellbores();

  return { wells, wellbores, fetchWells, wellsAndWellboresLoading };
};

export const useGetWellboreByWellboreId = (wellboreId?: string) => {
  const { wellbores, wellsAndWellboresLoading } = useFetchWellsWithWellbores();

  return {
    wellbore: useMemo(
      () => wellbores.find(wellbore => wellbore.wellboreId === wellboreId),
      [wellboreId, wellbores],
    ),
    wellboreLoading: wellsAndWellboresLoading,
  };
};

export const useGetSensorWellbores = () => {
  const { wellbores, wellsAndWellboresLoading, fetchWells } = useFetchWellsWithWellbores();
  const sensorWellbores = useMemo(() => {
    return wellbores.filter(
      wellbore => wellbore.wellboreId !== undefined,
    ) as Array<InnerSensorWellboreWithWellInfo>;
  }, [wellbores]);

  return {
    wellbores: sensorWellbores,
    wellboresLoading: wellsAndWellboresLoading,
    fetchWells,
  };
};

export const useGetWellsByWellIdsAndTuwis = ({
  wellIds = [],
  tuwis = [],
}: {
  wellIds?: string[];
  tuwis?: string[];
}) => {
  const { wells, wellsAndWellboresLoading } = useFetchWellsWithWellbores();

  return {
    wells: wells.filter(
      well =>
        (well.tuwi && tuwis.includes(well.tuwi)) || (well.wellId && wellIds.includes(well.wellId)),
    ),
    wellsAndWellboresLoading,
  };
};
